import React, { useState, useEffect } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    FileText,
    Ban,
    DollarSign,
    Clock,
    CheckCircle,
    X,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "sonner";
import { useLanguage } from "../../../context/LanguageContext";
import { baseURL } from "@/config/APIConfig";
import { defaultMetadata, getInvoiceColumns } from "./getInvoiceColumns";
import { DataTable } from "@/components/ui/table";
import Pagination from "../../PortPlace/Pagination";
import EditInvoiceModel from "./EditInvoiceModel";
import ExportDialog from "@/components/ExportDialog/ExportDialog";
import { generateExportContent } from "./generateExportContent";
import InvoicePrintView from "./InvoicePrintView";
import { InvoiceTechnicalHelpDialog } from "./InvoiceTechnicalHelpDialog";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { localeData } from "moment";
import { callLoader } from "@/redux/Loader/actions";
import { showToast } from "@/redux/Toast/actions";
import { getBookingDetails, addBookingDetails, updateBookingDetails, SyncArAp } from '@/redux/Booking/actions';
import { meta } from "@eslint/js";
import { set } from "date-fns";
import ConfirmSyncDialog from "@/components/ui/ConfirmSyncModal";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const STATUS_CONFIG = {
    draft: { label: "Draft", color: "bg-slate-500", icon: Edit2 },
    issued: { label: "Issued", color: "bg-blue-500", icon: FileText },
    partially_paid: { label: "Partial", color: "bg-amber-500", icon: Clock },
    paid: { label: "Paid", color: "bg-green-500", icon: CheckCircle },
    void: { label: "Void", color: "bg-red-500", icon: Ban },
};

const TYPE_CONFIG = {
    INVOICE: { label: "Invoice", color: "bg-blue-500" },
    "CREDIT MEMO": { label: "Credit Memo", color: "bg-amber-500" },
    "DEBIT MEMO": { label: "Debit Memo", color: "bg-red-500" },
};

const CustomerInvoices = () => {
    const { t } = useLanguage();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [summary, setSummary] = useState(null);
    const [showExport, setShowExport] = useState("");
    const [printData, setPrintData] = useState(null);
    const [metadata, setMetadata] = useState({ ...defaultMetadata });
    const [showLinkChargesDialog, setShowLinkChargesDialog] = useState(false);
    const [techGuideOpen, setTechGuideOpen] = useState(false);
    const [charges, setCharges] = useState([]);
    const [bookingData, setBookingData] = useState({});
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const username = authUser?.firstName + ' ' + authUser?.lastName || authUser?.username || "SYSTEM";

    //for the confirmaton popup when sync to ar/ap
    const [showSyncDialog, setShowSyncDialog] = useState(false);
    const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
    const [autoSync, setAutoSync] = useState(false);




    const getFormattedDate = (date = new Date(), offsetDays = 0) => {
        const d = new Date(date);
        d.setDate(d.getDate() + offsetDays);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0"); // months 0-11
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`; // YYYY-MM-DD
    };

    // Usage in state
    const [formData, setFormData] = useState({
        invoice_date: getFormattedDate(), // Today
        due_date: getFormattedDate(new Date(), 30), // 30 days later
        customer_id: "",
        customer_name: "",
        customer_invoice_no: "",
        invoice_type: "INVOICE",
        currency_code: "USD",
        exchange_rate: 1,
        terms: "Net 30",
        notes: "",
        status: "draft",
        lines: [],
    });



    const dispatch = useDispatch();
    useEffect(() => {
        fetchInvoices();
        fetchSummary();
    }, [filterStatus, filterType, searchTerm]);

    const fetchInvoices = async () => {
        try {
            dispatch(callLoader(1));
            let url = `${API_URL}/api/accounting/invoices?`;
            if (filterStatus) url += `status=${filterStatus}&`;
            if (filterType) url += `invoice_type=${filterType}&`;

            const response = await fetch(url);
            const data = await response.json();

            // Filter by search term on client side
            let filtered = data;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                filtered = data.filter(
                    (inv) =>
                        inv.invoice_no?.toLowerCase().includes(term) ||
                        inv.customer_name?.toLowerCase().includes(term),
                );
            }

            setInvoices(filtered);
        } catch (error) {
            console.error("Error fetching invoices:", error);
            toast.error("Failed to load invoices");
        } finally {
            setLoading(false);
            dispatch(callLoader(0));
        }
    };

    const fetchSummary = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/summary`);
            const data = await response.json();
            setSummary(data);
        } catch (error) {
            console.error("Error fetching summary:", error);
        }
    };


    const handleIssue = async (id) => {
        try {
            const response = await fetch(
                `${API_URL}/api/accounting/invoices/${id}/issue`,
                {
                    method: "POST",
                },
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || "Failed to issue invoice");
            }

            toast.success("Invoice issued!");
            fetchInvoices();
            fetchSummary();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleVoid = async (id) => {
        if (!window.confirm("Are you sure you want to void this invoice?")) return;

        try {
            const response = await fetch(
                `${API_URL}/api/accounting/invoices/${id}/void`,
                {
                    method: "POST",
                },
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || "Failed to void invoice");
            }

            toast.success("Invoice voided!");
            fetchInvoices();
            fetchSummary();
        } catch (error) {
            toast.error(error.message);
        }
    };

    function transformInvoiceToArCharges(invoice, type, isync = false) {
        if (!invoice || !Array.isArray(invoice.lines)) {
            return { bookingId: 0, arCharges: [] };
        }

        const isDelete = type === "delete";

        return {
            isSync: isync,
            bookingId: Number(invoice.booking_id) || 0,
            arCharges: invoice.lines.map((line) => ({
                id: Number(line?.source_charge_id) || 0,
                chargeCode: String(line?.charge_code_id || ""),
                description: String(line?.description || ""),
                currency: invoice?.currency_code || "USD",
                roe: String(line?.roe) || "1",
                basis: line?.Basis || "",
                container: "",
                quantity: String(line?.quantity) || 0,
                billToParty: invoice?.customer_name || "",
                billToPartyId: Number(invoice?.customer_id) || 0,
                billToPartyRef: String(invoice?.vendor_invoice_no || ""),
                amount: String(line?.amount) || 0,
                sellRate: Number(line?.unit_price) || 0,
                ppcc: "P",
                invoiceStatus: isDelete ? "" : invoice?.status || "",
                invoiceId: isDelete ? "" : line?.id || "",
                invoiceNo: isDelete ? "" : invoice?.invoice_no || "",
                createdBy: username || "SYSTEM",
                isactive: true
            }))
        };
    }

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this invoice?")) {
            return;
        }

        try {
            // 1️⃣ Get invoice data
            const getInvoiceResponse = await fetch(`${API_URL}/api/accounting/invoices/${id}`);

            if (!getInvoiceResponse.ok) {
                throw new Error("Failed to fetch invoice");
            }

            const invoice = await getInvoiceResponse.json();

            // 2️⃣ Transform payload
            const payload = transformInvoiceToArCharges(invoice, "delete", false);

            // 3️⃣ Sync AR/AP
            const bookingResponse = await SyncArAp(payload);

            if (bookingResponse?.statuscode === 200 || bookingResponse?.statuscode === 201) {

                // 4️⃣ Delete invoice
                const deleteResponse = await fetch(
                    `${API_URL}/api/accounting/invoices/${id}`,
                    { method: "DELETE" }
                );

                if (!deleteResponse.ok) {
                    const error = await deleteResponse.json();
                    throw new Error(error?.detail || "Failed to delete invoice");
                }

                toast.success("Invoice deleted!");
                fetchInvoices();
                fetchSummary();
            } else {
                throw new Error("SyncArAp failed");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Something went wrong");
        }
    };


    const openEditModal = async (invoice) => {
        try {
            const response = await fetch(
                `${API_URL}/api/accounting/invoices/${invoice.id}`
            );

            if (!response.ok) {
                throw new Error("Failed to fetch invoice");
            }

            const data = await response.json();
            console.log("invoice details:", data);

            setEditingInvoice(data);

            // ✅ Step 1: Set raw data
            setFormData((prev) => ({
                ...data,

                // ✅ Step 2: Override / format only required fields
                invoice_date: data.invoice_date || "",
                due_date: data.due_date || "",
                customer_id: data.customer_id || "",
                customer_name: data.customer_name || "",
                invoice_type: data.invoice_type || "INVOICE",
                currency_code: data.currency_code || "USD",
                exchange_rate: data.exchange_rate ?? 1,
                terms: data.terms || "",
                notes: data.notes || "",
                lines: transformLines(data.lines) || [],
                metadata: data.metadata || {}
            }));

            setMetadata(data.metadata || {});
            setShowModal(true);

        } catch (error) {
            console.error(error);
            toast.error("Failed to load invoice details");
        }
    };

    const resetForm = () => {
        const today = new Date();
        const dueDate = new Date(today);
        dueDate.setDate(today.getDate() + 30);

        setFormData({
            invoice_date: today.toISOString().split("T")[0],
            due_date: dueDate.toISOString().split("T")[0],
            customer_id: "",
            customer_name: "",
            invoice_type: "INVOICE",
            currency_code: "USD",
            exchange_rate: 1,
            terms: "Net 30",
            notes: "",
            status: "draft",
            lines: [],
        });

        setEditingInvoice(null);
        setMetadata({
            ...defaultMetadata,
            type: "INVOICE",
        });
        setShowModal(true);

        toast.success("Ready to create a new invoice");
    };


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const getStatusCounts = () => {
        const counts = { all: invoices.length };
        Object.keys(STATUS_CONFIG).forEach((status) => {
            counts[status] = invoices.filter((inv) => inv.status === status).length;
        });
        return counts;
    };

    const statusCounts = getStatusCounts();
    const totalOutstanding = invoices
        .filter((inv) => !["paid", "void"].includes(inv.status))
        .reduce((sum, inv) => sum + (inv.balance_due || 0), 0);

    const onMail = (invoice) => {
        setShowExport("email");
        generatePrintData(invoice);
    };

    const onPrint = (invoice) => {
        setShowExport("print");
        generatePrintData(invoice);
    };

    const generatePrintData = (invoice) => {
        //  const { invoice: fullInvoice, lines } = await fetchCustomerInvoice(invoice.id);
        //   const metadata = (fullInvoice?.metadata || {}) as Record<string, any>;
        console.log("invoice", invoice)

        const metadata = invoice?.metadata;
        const fullInvoice = {};

        const printData = {
            invoiceNo: invoice?.invoice_no,
            invoiceDate: invoice?.invoice_date,
            dueDate: invoice?.due_date,
            terms: invoice?.terms || "Net 30",
            invoiceType: invoice?.nvoice_type || "INVOICE",
            status: invoice?.status || "draft",
            customer: {
                name: invoice.customer_name || "-",
                address: metadata?.customer_address || "",
                phone: metadata?.customer_phone || "",
                email: metadata?.customer_email || "",
                taxId: metadata?.customer_tax_id || "",
            },
            bookingNo: metadata?.booking_no || "",
            originPort:
                fullInvoice?.origin_port?.port_name || metadata?.origin_port || "",
            destinationPort:
                fullInvoice?.destination_port?.port_name ||
                metadata?.destination_port ||
                "",
            division: metadata?.division || "",
            vessel: metadata?.vessel || "",
            weight: metadata?.weight,
            measurement: metadata?.measurement,
            customerRefNo: metadata?.customer_ref_no || "",
            onBoardDate: metadata?.on_board_date || "",
            description: metadata?.description || "",
            lines: (invoice?.lines || []).map((l) => ({
                description: l.description,
                chargeCode: l.charge_code_id || "",
                currency: invoice?.currency_code || "USD",
                roe: l.exchange_rate || 1,
                quantity: l.quantity || 1,
                rate: l.unit_price || 0,
                totalAmount: l.total_amount || 0,
            })),
            subtotal: invoice?.subtotal || 0,
            taxAmount: invoice?.tax_amount || 0,
            totalAmount: invoice?.total_amount || 0,
            balanceDue: fullInvoice?.balance_due || 0,
            paidAmount: fullInvoice?.paid_amount || 0,
            showWireTransfer: metadata?.show_wire_transfer ?? true,
            showZelleDetails: metadata?.show_zelle_details ?? false,
            showCreditCardDetails: metadata?.show_credit_card_details ?? false,
            notes: invoice?.notes || "",
            internalNotes: invoice?.internal_notes || "",
        };
        setPrintData({ ...printData });
    };
    const columns = getInvoiceColumns({
        openEditModal,
        handleIssue,
        handleDelete,
        handleVoid,
        formatDate,
        formatCurrency,
        STATUS_CONFIG,
        TYPE_CONFIG,
        onMail,
        onPrint,
    });

    const changeHandler = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };


    const transformLines = (lines = []) => {
        return lines.map((item, index) => {
            const quantity = Number(item.quantity) || 1;
            const unitPrice = Number(item.unit_price ?? item.amount) || 0;
            const amount = Number(item.amount) || (quantity * unitPrice);
            const taxRate = Number(item.tax_rate) || 0;
            const taxAmount = Number(item.tax_amount) || 0;
            const totalAmount = Number(item.total_amount) || (amount + taxAmount);

            return {
                line_no: item.line_no ?? 0,
                description: item.description || "",

                // chargeCode: item.chargeCode || "",
                charge_code_id: String(item.chargeCode || ""),
                gl_account_id: item.gl_account_id || "",

                // ✅ NEW FIELDS
                roe: item.roe != null ? Number(item.roe) : null,
                Basis: item.Basis || item.basis || "Per Shipment",

                quantity: quantity,
                unit_price: unitPrice,
                amount: amount,

                tax_rate: taxRate,
                tax_amount: taxAmount,
                total_amount: totalAmount,

                currency: item.currency || "",
                exchange_rate: item.roe != null ? Number(item.roe) : null,

                source_charge_id: item.chargeId ? String(item.chargeId) : item.source_charge_id ? String(item.source_charge_id) : "",
                booking_id: item.booking_id || "",

                comments: item.comments || null
            };
        });
    };
    const location = useLocation();

    const prefillData_ = location.state?.prefillData;

    const setPrefillData = (prefillData) => {
        console.log("data is comeing from job casting:", prefillData);
        setEditingInvoice(null);

        setMetadata({
            ...defaultMetadata,
            //left column
            file_reference_no: prefillData?.fileNumber,
            customer_po_number: prefillData?.poNumber,
            BookingNumber: prefillData?.carrierBookingNumber,
            origin_name: prefillData?.portOfLoading,
            origin_code: prefillData?.portOfLoadingCode,
            destination_name: prefillData?.portOfDischarge,
            destination_code: prefillData?.portOfDischargeCode,
            descriptions: `${prefillData?.vessel || ""} / ${prefillData?.vesselCode || ""}`,

            //right column
            division: "",
            type: "INVOICE",
            office_type: prefillData?.officeType,
            on_board_date: "",
            eta_date: prefillData?.etaAtDestination,
            weight_kgs: prefillData?.weight,
            measurement_cbm: prefillData?.measurement,
            show_wire_transfer: false,
            show_zelle_details: false,
            show_credit_card_details: false,
            email: prefillData?.email,
            profit_center: "",


        });

        setFormData({
            ...formData,

            invoice_date: new Date().toISOString().split("T")[0],
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            customer_id: prefillData?.billToPartyId,
            customer_name: prefillData?.billToParty,
            vendor_invoice_no: prefillData?.billToPartyRef || "",
            invoice_type: "INVOICE",
            currency_id: "USD",
            currency_code: "USD",
            exchange_rate: 1,
            terms: "Net 30",
            notes: "",
            lines: transformLines(prefillData?.charges),
            booking_id: String(prefillData?.id),
            metadata: metadata || {}
        });


        console.log("check data is correct or not:", transformLines(prefillData?.charges));
        setShowModal(true);
    }

    useEffect(() => {
        if (!prefillData_) return;
        setPrefillData(prefillData_);

    }, [prefillData_]);



    // const handleSubmit = async (e) => {
    //     // e.preventDefault();

    //     try {
    //         dispatch(callLoader(1));

    //         // ✅ Validate lines
    //         if (!formData?.lines || formData.lines.length === 0) {
    //             throw new Error("Invoice must have at least one line");
    //         }

    //         // ✅ Calculate totals safely
    //         const subtotal = formData.lines.reduce(
    //             (sum, line) => sum + (Number(line?.amount) || 0),
    //             0
    //         );

    //         const taxAmount = formData.lines.reduce(
    //             (sum, line) => sum + (Number(line?.tax_amount) || 0),
    //             0
    //         );

    //         const totalAmount = subtotal + taxAmount;

    //         // ✅ Prepare payload
    //         const payload = {
    //             ...formData,
    //             customer_id: String(formData?.customer_id || ""),
    //             lines: formData.lines,
    //             subtotal,
    //             tax_amount: taxAmount,
    //             total_amount: totalAmount,
    //             metadata: metadata || {}
    //         };

    //         console.log("Submitting payload:", payload);

    //         // ✅ API URL
    //         const url = editingInvoice
    //             ? `${API_URL}/api/accounting/invoices/${editingInvoice.id}`
    //             : `${API_URL}/api/accounting/invoices`;

    //         // ✅ Save invoice
    //         const response = await fetch(url, {
    //             method: editingInvoice ? "PUT" : "POST",
    //             headers: { "Content-Type": "application/json" },
    //             body: JSON.stringify(payload),
    //         });

    //         // ✅ Handle API error
    //         if (!response.ok) {
    //             let errorMessage = "Failed to save invoice";
    //             try {
    //                 const error = await response.json();
    //                 errorMessage = error?.detail || errorMessage;
    //             } catch {
    //                 // ignore JSON parse error
    //             }
    //             throw new Error(errorMessage);
    //         }

    //         const resJson = await response.json();

    //         // ✅ Transform for AR/AP sync
    //         const syncPayload = transformInvoiceToArCharges(resJson, "create", true);

    //         // ✅ Sync AR/AP
    //         const bookingResponse = await SyncArAp(syncPayload);

    //         if (
    //             bookingResponse?.statuscode === 200 ||
    //             bookingResponse?.statuscode === 201
    //         ) {
    //             console.log("✅ SyncArAp successful");
    //         } else {
    //             console.error("❌ SyncArAp failed:", bookingResponse);
    //             toast.error("Invoice saved but sync failed");
    //         }

    //         // ✅ Success toast ONLY after success
    //         dispatch(
    //             showToast(
    //                 editingInvoice
    //                     ? "Invoice updated successfully"
    //                     : "Invoice created successfully",
    //                 "success",
    //                 true
    //             )
    //         );

    //         // ✅ Reset UI
    //         setTimeout(() => {
    //             setShowModal(false);
    //             setEditingInvoice(null);
    //             resetForm();
    //             fetchInvoices();
    //             fetchSummary();
    //         }, 1500);

    //     } catch (error) {
    //         console.error("❌ Submit Error:", error);
    //         toast.error(error.message || "Something went wrong");
    //     } finally {
    //         dispatch(callLoader(0));
    //     }
    // };
    const handleSubmit = async () => {
        try {
            dispatch(callLoader(1));

            if (!formData?.lines || formData.lines.length === 0) {
                throw new Error("Invoice must have at least one line");
            }

            const subtotal = formData.lines.reduce(
                (sum, line) => sum + (Number(line?.amount) || 0),
                0
            );

            const taxAmount = formData.lines.reduce(
                (sum, line) => sum + (Number(line?.tax_amount) || 0),
                0
            );

            const totalAmount = subtotal + taxAmount;

            const payload = {
                ...formData,
                customer_id: String(formData?.customer_id || ""),
                lines: formData.lines,
                subtotal,
                tax_amount: taxAmount,
                total_amount: totalAmount,
                metadata: metadata || {}
            };

            const url = editingInvoice
                ? `${API_URL}/api/accounting/invoices/${editingInvoice.id}`
                : `${API_URL}/api/accounting/invoices`;

            const response = await fetch(url, {
                method: editingInvoice ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Failed to save invoice");
            }

            const resJson = await response.json();

            // ✅ Open dialog
            if (autoSync) {
                await handleSyncDecision(true);
            } else {
                setPendingInvoiceData(resJson);
                setShowSyncDialog(true);
            }

        } catch (error) {
            console.error(error);
            toast.error(error.message);
            dispatch(callLoader(0));
        }
        finally {
            dispatch(callLoader(0));
        }
    };

    const handleSyncDecision = async (isSync) => {
        try {
            // ✅ Always create payload with isSync
            const syncPayload = transformInvoiceToArCharges(
                pendingInvoiceData,
                "create",
                isSync
            );

            let bookingResponse = null;

            if (isSync) {
                bookingResponse = await SyncArAp(syncPayload);

                const status =
                    bookingResponse?.statuscode || bookingResponse?.statusCode;

                if (status === 200 || status === 201) {
                    console.log("✅ Sync success");
                } else {
                    toast.error("Sync failed");
                }
            }

            dispatch(
                showToast(
                    isSync
                        ? "Invoice saved & synced"
                        : "Invoice saved (sync skipped)",
                    "success",
                    true
                )
            );

            setTimeout(() => {
                setShowModal(false);
                setEditingInvoice(null);
                resetForm();
                fetchInvoices();
                fetchSummary();
            }, 1200);

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setShowSyncDialog(false);
            setPendingInvoiceData(null);
            dispatch(callLoader(0));
        }
    };





    const loadBookingData = async (id) => {
        console.log("loading booking data for id:", id);
        dispatch(callLoader(1));
        try {
            const data = await dispatch(getBookingDetails(id,authUser?.companyId));
            if (data) {
                if (data.formSnapshot) {
                    const snapshot =
                        typeof data.formSnapshot === "string"
                            ? JSON.parse(data.formSnapshot)
                            : data.formSnapshot;

                    setData(snapshot, snapshot.arCharges || []);
                } else if (data.arCharges || data.apCharges) {

                    setData(data, data.arCharges || []);
                }
            }
        } catch (error) {
            console.error("Error loading booking:", error);
        } finally {

            dispatch(callLoader(0));
        }
    };

    const getVendorFromCharges = (apCharges = []) => {
        const found = (apCharges || []).find(
            (item) => item.billToPartyId && item.billToParty,
        );

        if (found) {
            return {
                billToParty: found.billToParty,
                billToPartyId: found.billToPartyId,
                billToPartyRef: found.billToPartyRef || "",
            };
        }

        return {
            billToParty: "",
            billToPartyId: 0,
            billToPartyRef: "",
        };
    };

    function setData(data, apCharges) {
        const vendorDetails = getVendorFromCharges(apCharges || [])
        const char_ = apCharges.map((c, i) => ({
            ...c,
            line_no: i + 1,
            description: c.description,
            quantity: Number(c?.quantity),
            unit_price: Number(c?.amount),
            amount: Number(c?.quantity || 0) * Number(c?.amount),
            tax_rate: 0,
            tax_amount: 0,
            total_amount: Number(c?.quantity || 0) * Number(c?.amount),
            roe: c?.roe,
            Basis: c?.basis,
            currency: c?.currency,
            source_charge_id: String(c?.id || ""),
            ...vendorDetails
        }))
        data.charges = char_;
        setPrefillData(data)

    }

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header with Gradient */}
            <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-teal-800 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t("customerInvoices")}</h1>
                            <p className="text-slate-300">{t("manageAR")}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex gap-2">
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <span
                                    key={key}
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${config.color} bg-opacity-80`}
                                >
                                    {config.label}: {statusCounts[key] || 0}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div
                    className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-slate-500 cursor-pointer hover:shadow-md ${filterStatus === "" ? "ring-2 ring-blue-500" : ""
                        }`}
                    onClick={() => setFilterStatus("")}
                    data-testid="filter-all-invoices"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t("allInvoices")}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {statusCounts.all}
                            </p>
                        </div>
                        <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                </div>

                <div
                    className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-slate-400 cursor-pointer hover:shadow-md ${filterStatus === "draft" ? "ring-2 ring-blue-500" : ""
                        }`}
                    onClick={() => setFilterStatus("draft")}
                    data-testid="filter-draft-invoices"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t("draft")}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {statusCounts.draft || 0}
                            </p>
                        </div>
                        <Edit2 className="w-8 h-8 text-slate-400" />
                    </div>
                </div>

                <div
                    className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md ${filterStatus === "issued" ? "ring-2 ring-blue-500" : ""
                        }`}
                    onClick={() => setFilterStatus("issued")}
                    data-testid="filter-issued-invoices"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t("outstanding")}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {statusCounts.issued || 0}
                            </p>
                        </div>
                        <Clock className="w-8 h-8 text-blue-400" />
                    </div>
                </div>

                <div
                    className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-green-500 cursor-pointer hover:shadow-md ${filterStatus === "paid" ? "ring-2 ring-blue-500" : ""
                        }`}
                    onClick={() => setFilterStatus("paid")}
                    data-testid="filter-paid-invoices"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t("paid")}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {statusCounts.paid || 0}
                            </p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-amber-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t("totalOutstanding")}
                            </p>
                            <p className="text-xl font-bold text-amber-600">
                                {formatCurrency(totalOutstanding)}
                            </p>
                        </div>
                        <DollarSign className="w-8 h-8 text-amber-400" />
                    </div>
                </div>
            </div>

            {/* Filters & Actions */}
            <div className="rounded-lg border text-card-foreground shadow-sm overflow-hidden bg-card/50 backdrop-blur-sm">
                <div className="flex flex-wrap gap-4 items-center justify-between  p-4">
                    <div className="flex flex-wrap gap-4 items-center flex-1">
                        <div className="flex-1 min-w-[200px] max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder={t("searchInvoices")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-invoices"
                                />
                            </div>
                        </div>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                            data-testid="filter-status"
                        >
                            <option value="">{t("allStatuses")}</option>
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>
                                    {config.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                            data-testid="filter-invoice-type"
                        >
                            <option value="">{t("allTypes")}</option>
                            {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>
                                    {config.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Button
                        onClick={() => {
                            resetForm();
                            setEditingInvoice(null);
                            setShowModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="new-invoice-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {t("newInvoice")}
                    </Button>
                </div>

                {/* Invoices Table */}
                <DataTable
                    loading={loading}
                    data={invoices || []}
                    columns={columns}
                    boxClass="bg-transaprent"
                    trClass={
                        "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    }
                    thClass={
                        "px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 white whitespace-nowrap uppercase tracking-wider"
                    }
                    tdClass=" text-sm  px-2 py-2 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
                    htrClass="border-b border-slate-200 dark:border-slate-800 bg-transparen"
                    emptyText="No invoices found"
                />
            </div>
            {/* <Pagination
          className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-0 bg-slate-50 dark:bg-slate-800/50 mt-8"
          currentPage={currentPage}
          recordsPerPage={pageSize}
          setRecordsPerPage={handlePageSize}
          onPrevious={changePage}
          onNext={changePage}
          totalRecord={pagination?.totalRecord}
          handleGoto={changePage}
          title="load plans"
        /> */}
            {/* Invoice Form Modal */}
            {showModal && (
                <EditInvoiceModel
                    onClose={() => {
                        setShowModal(false);
                        setEditingInvoice(null);
                    }}
                    editingInvoice={editingInvoice}
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    changeHandler={changeHandler}
                    onDelete={(data) => {
                        handleDelete(editingInvoice.id);
                    }}
                    metadata={metadata}
                    setMetadata={setMetadata}
                    setShowLinkChargesDialog={setShowLinkChargesDialog}
                    handlePrint={() => onPrint(editingInvoice)}
                    handleEmail={() => onMail(editingInvoice)}
                    handleTechGuide={() => setTechGuideOpen(true)}
                    handleInvoiceLog={() => { }}
                    handleNewInvoice={() => resetForm()}
                    loading={false}
                    fetchBook={loadBookingData}
                />
            )}

            <ExportDialog
                open={!!showExport}
                onOpenChange={() => {
                    setShowExport("");
                    setPrintData(null);
                }}
                mode={showExport}
                email={{
                    subject: `Invoice ${printData?.invoiceNo} - ${printData?.customer.name}`,
                    body: `Dear ${printData?.customer.name},\n\nPlease find attached Invoice ${printData?.invoiceNo} for the amount of $${printData?.totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}.\n\nPayment is due by ${printData?.dueDate}.\n\nBest regards,\nShipez.ai Team`,
                }}
                data={printData}
                pageTitle={`${printData?.invoiceType || "Invoice"} - ${printData?.invoiceNo}`}
                heading="Invoice"
                ExportFileContent={generateExportContent}
            >
                <InvoicePrintView />
            </ExportDialog>
            <InvoiceTechnicalHelpDialog
                open={techGuideOpen}
                onOpenChange={setTechGuideOpen}
            />


            <ConfirmSyncDialog
                open={showSyncDialog}
                onClose={setShowSyncDialog}
                onConfirm={handleSyncDecision}
                title="Sync Invoice?"
                description="Do you want to sync this invoice with shipment data?"
            />


        </div>
    );
};

export default CustomerInvoices;
